import {
  buttonClasses,
  checkboxClasses,
  formLabelClasses,
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
  radioClasses,
} from '@mui/material';
import {
  alpha,
  duration,
  type PaletteOptions,
  type SimplePaletteColorOptions,
  type ThemeOptions,
} from '@mui/material/styles';
import type { CustomStyling } from './theme.types';

const customColors: PaletteOptions['customColors'] = {
  lightCopper: '#FCF6F3',
  copper: '#BB4F09',
  gunMetal: '#2B2B2A',
  cream: '#CBC4C1',
  white50: '#FFFFFF',
  darkGray: '#6B6D70',
  lightGray: '#E4E4E4',
  lightBeige: '#F7F6F6',
  activeField: '#D2682A',
  errorRed: '#D82313',
  darkCopper: '#964A1E',
  success: '#00752C',
  orange100: '#ED6C02',
  disabledActionBar: '#A2A2A2',
  textNeutralTertiary: '#6B6B6A',
  textNeutralPrimary: '#1A1A19',
  blue: '#3272D5',
  lightYellow: '#FFFAF2',
  beigeLight: '#EED7C5',
  payrollWorksheetHeader: '#283A5A',
  payrollWorksheetBorder: '#C4C4C4',
  payrollWorksheetDivider: '#E0E0E0',
  softWhite: '#FAF9F9',
};

const customStyling: CustomStyling = {
  baseButtonStyle: {
    width: '100%',
    gap: '.5em',
    px: '1.5em',
    py: '.75em',
    fontSize: (theme) => theme.typography.button.fontSize,
    lineHeight: (theme) => theme.typography.button.lineHeight,
    fontWeight: (theme) => theme.typography.button.fontWeight,
    textTransform: 'none',
    transitionProperty: `background, color, borderColor`,
    transitionDuration: (theme) => `${theme.transitions.duration.standard}ms`,
    transitionDelay: '50ms',

    [`.${buttonClasses.icon}`]: {
      margin: '0',
    },

    path: {
      stroke: 'currentColor',
    },
  },
  appBar: {
    height: '64px',
    secondaryHeight: '48px',
  },
  footer: {
    height: '72px',
  },
  quoteStepper: {
    primary: customColors.copper,
    disabled: customColors.cream,
    completedText: customColors.gunMetal,
    completedIconCircle: customColors.white50,
    activeIconText: customColors.white50,
    progressBarBG: customColors.lightGray,
  },
  toggleButton: {
    borderColor: customColors.cream,
    selectedBorderColor: customColors.copper,
    color: customColors.gunMetal,
    selectedColor: customColors.copper,
  },
  toggleButtonsGroup: {
    color: customColors.gunMetal,
  },
  button: {
    primary: {
      backgroundColor: customColors.copper,
      hoverBackgroundColor: customColors.darkCopper,
    },
  },
  shadow: {
    coverageDetails: '0 -8px 24px 0 #00000029',
    quoteDetailsCard: '0 6px 16px 0 #0000001F',
    footer: '0px -4px 10px 0px #0000000A',
  },
};

const palette: PaletteOptions = {
  background: {
    default: '#fff',
  },
  mode: 'light',
  primary: {
    main: '#000000',
    light: '#414563',
  },
  secondary: {
    dark: '#913B04',
    main: customColors.copper as string,
  },
  error: {
    main: '#C9170B',
  },
  dialogCancel: {
    main: '#424242',
  },
  dialogAlert: {
    main: '#C9170B',
    light: '#C9170B',
    dark: '#C9170B',
    contrastText: '#C9170B',
  },
  dialogInfo: {
    main: customColors.copper as string,
  },
  customColors,
};

const themeOptions: ThemeOptions = {
  palette,
  customStyling,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 900,
      lg: 1200,
      xl: 1700,
    },
  },
  typography: {
    fontFamily: 'Nexa,sans-serif',
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: palette.customColors.activeField,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& label': {
            fontSize: '14px',
          },
          '& .MuiInputBase-input:not(textarea) ': {
            overflow: 'hidden',
          },
          // Hide autofill safari icon in input field
          '& ::-webkit-contacts-auto-fill-button': {
            visibility: 'hidden',
            display: 'none!important',
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
          },
        },

        input: {
          '::placeholder': {
            opacity: 1,
            color: 'rgba(0 0 0 / 0.6)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          '::placeholder': {
            opacity: 1,
            color: 'rgba(0 0 0 / 0.6)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          '&.Mui-focused': {
            '&.Mui-focused fieldset': {
              borderColor: palette.customColors.activeField,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '16px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.customColors.cream,
          transitionProperty: `color, opacity`,
          transitionDuration: `${duration.standard}ms`,

          [`&.${checkboxClasses.checked}`]: {
            color: palette.customColors.copper,
          },

          [`&.${checkboxClasses.disabled}`]: {
            opacity: '0.5',
          },

          ':has(input[readonly])': {
            opacity: '0.5',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: palette.customColors.darkGray,
          transitionProperty: `color, opacity`,
          transitionDuration: `${duration.standard}ms`,

          [`&.${radioClasses.checked}`]: {
            color: palette.customColors.copper,
          },

          [`&.${checkboxClasses.disabled}`]: {
            opacity: '0.5',
          },

          ':has(input[readonly])': {
            opacity: '0.5',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: '1.5rem',
          height: '1.5rem',
          boxSizing: 'content-box',
          transition: `color ${duration.standard}ms`,

          path: {
            stroke: 'currentcolor',
          },

          '&:hover': {
            color: palette.customColors.copper,
            backgroundColor: palette.customColors.lightBeige,
          },

          '&:active': {
            color: palette.customColors.darkGray,
            backgroundColor: 'transparent',
          },

          '&:disabled': {
            color: palette.customColors.cream,
          },
        },
      },
    },
  },
  mixins: {
    toolbar: {
      minHeight: 0,
    },
    primaryButtonStyle: {
      ...customStyling.baseButtonStyle,

      backgroundColor: customStyling.button.primary.backgroundColor,
      color: palette.customColors.white50,
      '&:hover': {
        background: customStyling.button.primary.hoverBackgroundColor,
      },
    },
    secondaryButtonStyle: {
      ...customStyling.baseButtonStyle,

      color: (theme) => theme.palette.customColors.copper,
      border: (theme) => `1px solid ${theme.palette.customColors.copper}`,
      backgroundColor: (theme) => theme.palette.customColors.white50,

      '&:hover': {
        background: (theme) => `${theme.palette.customColors.copper}`,
        color: (theme) => `${theme.palette.customColors.white50}`,
      },

      '&:disabled': {
        color: (theme) => theme.palette.customColors.cream,
        background: (theme) => theme.palette.customColors.lightGray,
        borderColor: (theme) => theme.palette.customColors.lightGray,
      },
    },
    tertiaryButtonStyle: {
      ...customStyling.baseButtonStyle,

      color: (theme) => theme.palette.customColors.copper,
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: 'transparent',
        color: palette.customColors.darkCopper,
      },

      '&:active': {
        color: palette.customColors.copper,
      },

      '&:disabled': {
        color: palette.customColors.cream,
      },
    },
    readOnlyInputStyle: {
      [`.${inputBaseClasses.root}.${outlinedInputClasses.root} fieldset`]: {
        borderColor: palette.customColors.lightBeige,
        borderWidth: '1px',
        '&:hover': {
          [`.${outlinedInputClasses.notchedOutline}`]: {
            borderColor: palette.customColors.lightBeige,
          },
        },
        [`.${outlinedInputClasses.notchedOutline}`]: {
          borderColor: palette.customColors.lightBeige,
        },
      },
      [`.${formLabelClasses.root}.${inputLabelClasses.root}.Mui-error`]: {
        color: (theme) => alpha(theme.palette.primary.main, 0.5),
      },
      borderRadius: 0,
      [`.${formLabelClasses.root}.${inputLabelClasses.root}.Mui-focused`]: {
        color: (theme) => alpha(theme.palette.primary.main, 0.5),
      },
      [`.${inputBaseClasses.root}.${outlinedInputClasses.root}.Mui-focused fieldset`]: {
        borderColor: palette.customColors.lightBeige,
        borderWidth: '1px',
      },
    },
    resetNumberInputStyle: {
      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
        display: 'none',
      },
      '& input[type=number]': {
        MozAppearance: 'textfield',
      },
    },
    placeholderColor: (formik, fieldName) => ({
      '& .MuiOutlinedInput-input::placeholder': {
        color:
          formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])
            ? (palette.error as SimplePaletteColorOptions).main
            : '',
      },
    }),
    errorIconStyle: {
      '& .error-icon-style': {
        span: {
          svg: {
            path: {
              stroke: (theme) => theme.palette.error.main,
            },
            circle: {
              stroke: (theme) => theme.palette.error.main,
            },
          },
        },
      },
    },
    textOverflowStyle: (maxWidthOptions) => ({
      maxWidth: {
        xs: '180px',
        sm: '500px',
        ...maxWidthOptions,
      },
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    }),
  },
};

export { palette, themeOptions };
